import { Button, Checkbox, EmptyState, Input, Modal, RichTextEditor, Select, Textarea, decorarLeituraRte, useToast } from '@orbind/ui'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useMemo, useRef, useState, type CSSProperties } from 'react'
import { useNavigate, useParams } from 'react-router'
import { api, apiBase, apiOrigin, webTokenStorage } from '../lib/api.ts'
import { useBootstrap } from '../lib/catalog.ts'
import { useSession } from '../lib/session.tsx'
import { initialsOf, useConfirm } from '../features/work-item/bits.tsx'
import { ExplorerBody, useExplorerConfig } from '../shell/explorer.tsx'
import { DocsEditor } from '../features/docs/DocsEditor.tsx'
import { TOC_MAX, TOC_MIN, larguraMaximaDoConteudo } from '../features/docs/conteudo.ts'
import './docs.css'

/**
 * Docs (SPEC-20260906-1215, modelo do Outline — referência docs.wynk.app.br):
 * COLEÇÕES agrupam documentos e definem permissões; documentos ANINHAM em
 * documentos (não há subpastas). Editor rich text com menu "/", atalhos
 * markdown e bolha de formatação; compartilhar por membro + link público;
 * exportar Word/PDF; duplicar; estatísticas e sumário do documento.
 */

type Nivel = 'view' | 'edit' | 'delete' | 'owner'
type DocLinha = {
  id: string
  folderId: string | null
  parentDocumentId: string | null
  title: string
  ownerMemberId: string
  /** Preenchido = este documento E a capa da colecao (abre ao clicar nela). */
  coverFolderId: string | null
  /** 'todos' = todo mundo com acesso; 'escolhidos' = so dono + convidados. */
  shareMode: 'todos' | 'escolhidos'
  publishedVersion: number
  temLink: boolean
  updatedAt: string
  meuNivel: Nivel
}
type Colecao = {
  id: string
  name: string
  parentFolderId: string | null
  sortOrder: number
  icon: string | null
  color: string | null
  allowPublicShare: boolean
  allowComments: boolean
  defaultLevel: 'none' | 'view' | 'edit'
}

const NIVEL_ROTULO: Record<string, string> = {
  view: 'Visualizar',
  edit: 'Editar',
  delete: 'Excluir',
  owner: 'Dono',
}

function formatData(iso: string) {
  const d = new Date(iso)
  return `${new Intl.DateTimeFormat('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' }).format(d)} ${new Intl.DateTimeFormat('pt-BR', { hour: '2-digit', minute: '2-digit' }).format(d)}`
}

/** Estatísticas do conteúdo (menu do Outline): palavras e minutos de leitura. */
function estatisticasDe(html: string) {
  const texto = html.replace(/<[^>]+>/g, ' ').replace(/&\w+;/g, ' ')
  const palavras = texto.split(/\s+/).filter(Boolean).length
  return { palavras, minutos: Math.max(1, Math.round(palavras / 200)) }
}

export function DocsPage() {
  const { id: docAbertoId = '' } = useParams()
  const navigate = useNavigate()
  const toast = useToast()
  const confirm = useConfirm()
  const qc = useQueryClient()
  const bootstrap = useBootstrap()
  const session = useSession()
  const meuId = session.id

  const arvore = useQuery({
    queryKey: ['documents'],
    queryFn: async () => (await api.documents.$get()).json(),
  })
  const invalidar = () => {
    void qc.invalidateQueries({ queryKey: ['documents'] })
    if (docAbertoId) void qc.invalidateQueries({ queryKey: ['document', docAbertoId] })
  }

  const colecoes: Colecao[] = ((arvore.data?.folders ?? []) as Colecao[]).filter((c) => c.parentFolderId === null)
  const docs: DocLinha[] = (arvore.data?.documents ?? []) as DocLinha[]

  // ------------------------------------------------------------- mutacoes
  const criarColecao = useMutation({
    mutationFn: async (input: { name: string }) =>
      (await api.documents.folders.$post({ json: { name: input.name } })).json(),
    onSuccess: invalidar,
    onError: (e) => toast.push({ tone: 'danger', message: e instanceof Error ? e.message : 'Falha ao criar a coleção.' }),
  })
  const apagarColecao = useMutation({
    mutationFn: async (id: string) => {
      const r = await api.documents.folders[':id'].$delete({ param: { id } })
      if (!r.ok) {
        const corpo = (await r.json().catch(() => null)) as { error?: { message?: string } } | null
        throw new Error(corpo?.error?.message ?? 'Falha ao excluir a coleção.')
      }
      return r.json()
    },
    onSuccess: invalidar,
    onError: (e) => toast.push({ tone: 'danger', message: e instanceof Error ? e.message : 'Falha ao excluir a coleção.' }),
  })
  const criarDoc = useMutation({
    mutationFn: async (input: { title: string; folderId?: string | null; shareMode?: 'todos' | 'escolhidos'; parentDocumentId?: string | null }) =>
      (await api.documents.$post({ json: { title: input.title, folderId: input.folderId ?? null, shareMode: input.shareMode ?? 'todos', parentDocumentId: input.parentDocumentId ?? null } })).json(),
    onSuccess: (d) => {
      invalidar()
      const novo = (d as { document?: { id: string } }).document
      if (novo) navigate(`/docs/${novo.id}`)
    },
    onError: (e) => toast.push({ tone: 'danger', message: e instanceof Error ? e.message : 'Falha ao criar o documento.' }),
  })

  // ------------------------------------------------- estado do explorer ---
  const [fechados, setFechados] = useState<ReadonlySet<string>>(new Set())
  const alternar = (id: string) =>
    setFechados((s) => {
      const n = new Set(s)
      if (n.has(id)) n.delete(id)
      else n.add(id)
      return n
    })
  const [novaColecao, setNovaColecao] = useState(false)
  const [nomeNovaColecao, setNomeNovaColecao] = useState('')
  const [editandoColecao, setEditandoColecao] = useState<Colecao | null>(null)

  useExplorerConfig({
    footer: `${docs.length} documento${docs.length === 1 ? '' : 's'} · ${colecoes.length} coleç${colecoes.length === 1 ? 'ão' : 'ões'}`,
    onCreate: () => setNovaColecao(true),
  })

  const filhosDe = (paiId: string) => docs.filter((d) => d.parentDocumentId === paiId)
  /** A CAPA da colecao (o doc que abre ao clicar no nome dela). */
  const capaDe = (colecaoId: string) => docs.find((d) => d.coverFolderId === colecaoId)
  const topoDaColecao = (colecaoId: string | null) =>
    docs.filter((d) => d.shareMode !== 'escolhidos' && d.folderId === colecaoId && !d.parentDocumentId && !d.coverFolderId)
  const meusPrivados = docs.filter((d) => d.shareMode === 'escolhidos' && d.ownerMemberId === meuId && !d.parentDocumentId)
  const compartilhadosComigo = docs.filter((d) => d.shareMode === 'escolhidos' && d.ownerMemberId !== meuId && !d.parentDocumentId)

  /** Linha de documento com os FILHOS aninhados (chevron abre/fecha). */
  const linhaDoc = (d: DocLinha, prof: number): React.ReactNode => {
    const filhos = filhosDe(d.id)
    const aberto = !fechados.has(d.id)
    return (
      <div key={d.id}>
        <div className={`dc__doc${docAbertoId === d.id ? ' dc__doc--on' : ''}`} style={{ paddingLeft: 8 + prof * 14 }}>
          {filhos.length > 0 ? (
            <button type="button" className="dc__doc-chev" aria-expanded={aberto} aria-label="Abrir/fechar aninhados" onClick={() => alternar(d.id)}>
              <i className={`fas fa-chevron-${aberto ? 'down' : 'right'}`} aria-hidden />
            </button>
          ) : (
            <span className="dc__doc-chev" aria-hidden />
          )}
          <button type="button" className="dc__doc-abrir" onClick={() => navigate(`/docs/${d.id}`)} title={d.title}>
            <i className={d.shareMode === 'escolhidos' ? 'fas fa-lock' : 'far fa-file-alt'} aria-hidden />
            <span className="dc__doc-nome">{d.title}</span>
            {d.meuNivel !== 'view' && d.publishedVersion === 0 ? (
              <span className="dc__doc-rascunho" title="Ainda não publicado — só quem edita vê">Rascunho</span>
            ) : null}
            {d.temLink ? <i className="fas fa-link dc__doc-link" aria-hidden title="Tem link público" /> : null}
          </button>
          {d.meuNivel !== 'view' ? (
            <span className="dc__pasta-acoes">
              <button type="button" title="Novo documento aninhado" aria-label={`Novo documento dentro de ${d.title}`}
                onClick={() => criarDoc.mutate({ title: 'Sem título', parentDocumentId: d.id })}>
                <i className="fas fa-file-medical" aria-hidden />
              </button>
            </span>
          ) : null}
        </div>
        {aberto ? filhos.map((f) => linhaDoc(f, prof + 1)) : null}
      </div>
    )
  }

  const linhaColecao = (c: Colecao) => {
    const aberta = !fechados.has(c.id)
    const doTopo = topoDaColecao(c.id)
    const capa = capaDe(c.id)
    return (
      <div key={c.id}>
        <div className={`dc__pasta${capa && docAbertoId === capa.id ? ' dc__pasta--on' : ''}`}>
          <button type="button" className="dc__pasta-tog" onClick={() => alternar(c.id)} aria-expanded={aberta}>
            <i className={`fas fa-chevron-${aberta ? 'down' : 'right'}`} aria-hidden />
            <i className={c.icon || 'fas fa-folder'} aria-hidden style={{ color: c.color ?? 'var(--ob-primary)' }} />
          </button>
          <button
            type="button"
            className="dc__pasta-nome"
            title="A coleção também é um documento — clique para abrir"
            onClick={() => (capa ? navigate(`/docs/${capa.id}`) : alternar(c.id))}
            onDoubleClick={() => setEditandoColecao(c)}
          >
            {c.name}
          </button>
          <span className="dc__pasta-acoes">
            <button type="button" title="Novo documento nesta coleção" aria-label={`Novo documento em ${c.name}`}
              onClick={() => criarDoc.mutate({ title: 'Sem título', folderId: c.id })}>
              <i className="fas fa-file-medical" aria-hidden />
            </button>
            <button type="button" title="Editar a coleção (nome, ícone e permissões)" aria-label={`Editar ${c.name}`}
              onClick={() => setEditandoColecao(c)}>
              <i className="fas fa-pen" aria-hidden />
            </button>
            <button type="button" title="Excluir a coleção (precisa estar vazia)" aria-label={`Excluir ${c.name}`}
              onClick={() => confirm.ask(`Excluir a coleção “${c.name}”? Só é possível quando está vazia.`, () => apagarColecao.mutate(c.id))}>
              <i className="far fa-trash-alt" aria-hidden />
            </button>
          </span>
        </div>
        {aberta ? doTopo.map((d) => linhaDoc(d, 1)) : null}
      </div>
    )
  }

  return (
    <div className="dc">
      <ExplorerBody>
        <div className="dc__arvore">
          <div className="dc__secao">
            <span className="dc__secao-tit">Coleções</span>
          </div>
          {colecoes.map(linhaColecao)}
          {topoDaColecao(null).map((d) => linhaDoc(d, 0))}
          <button type="button" className="dc__novacol" onClick={() => setNovaColecao(true)}>
            <i className="fas fa-plus" aria-hidden /> Nova coleção
          </button>

          <div className="dc__secao dc__secao--sep">
            <span className="dc__secao-tit">Meus documentos</span>
            <span className="dc__pasta-acoes dc__pasta-acoes--fixas">
              <button type="button" title="Novo documento de pessoas escolhidas (só você e quem convidar)" aria-label="Novo documento só meu"
                onClick={() => criarDoc.mutate({ title: 'Sem título', shareMode: 'escolhidos' })}>
                <i className="fas fa-file-medical" aria-hidden />
              </button>
            </span>
          </div>
          {meusPrivados.map((d) => linhaDoc(d, 0))}
          {meusPrivados.length === 0 ? <p className="dc__vazio">Documentos só seus — você escolhe quem vê.</p> : null}

          {compartilhadosComigo.length > 0 ? (
            <>
              <div className="dc__secao dc__secao--sep">
                <span className="dc__secao-tit">Compartilhados comigo</span>
              </div>
              {compartilhadosComigo.map((d) => linhaDoc(d, 0))}
            </>
          ) : null}
        </div>
      </ExplorerBody>

      {docAbertoId ? (
        <DocumentoAberto
          key={docAbertoId}
          docId={docAbertoId}
          colecoes={colecoes}
          aoMudar={invalidar}
          aoExcluir={() => navigate('/docs')}
          aoAbrir={(id) => navigate(`/docs/${id}`)}
          membros={(bootstrap.data?.members ?? []).filter((m) => m.isActive)}
          meuId={meuId}
        />
      ) : (
        <EmptyState
          icon="far fa-file-alt"
          title="Documentos"
          description={
            docs.length === 0
              ? 'Crie a primeira coleção e o primeiro documento no painel ao lado.'
              : 'Escolha um documento no painel ao lado — ou crie um novo.'
          }
        />
      )}

      {novaColecao ? (
        <CriarColecaoModal
          onClose={() => setNovaColecao(false)}
          aoMudar={invalidar}
          aoCriar={(coverDocId) => navigate(`/docs/${coverDocId}`)}
        />
      ) : null}
      {editandoColecao ? (
        <EditarColecaoModal
          colecao={editandoColecao}
          onClose={() => setEditandoColecao(null)}
          aoMudar={invalidar}
        />
      ) : null}
      {confirm.element}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Criar uma coleção (modal do Outline): nome, permissão e toggles
// ---------------------------------------------------------------------------

const OPCOES_PERMISSAO = [
  { value: 'view', label: 'Visualizar' },
  { value: 'edit', label: 'Editar' },
  { value: 'none', label: 'Sem acesso' },
]

function CriarColecaoModal({ onClose, aoMudar, aoCriar }: { onClose: () => void; aoMudar: () => void; aoCriar?: (coverDocId: string) => void }) {
  const toast = useToast()
  const [nome, setNome] = useState('')
  const [permissao, setPermissao] = useState<'none' | 'view' | 'edit'>('view')
  const [publico, setPublico] = useState(true)
  const [comentando, setComentando] = useState(true)
  const criar = useMutation({
    mutationFn: async () =>
      (
        await api.documents.folders.$post({
          json: { name: nome.trim(), defaultLevel: permissao, allowPublicShare: publico, allowComments: comentando },
        })
      ).json(),
    onSuccess: (d) => {
      toast.push({ tone: 'success', message: 'Coleção criada.' })
      aoMudar()
      onClose()
      const capa = (d as { coverDocumentId?: string }).coverDocumentId
      if (capa) aoCriar?.(capa)
    },
    onError: (e) => toast.push({ tone: 'danger', message: e instanceof Error ? e.message : 'Falha ao criar a coleção.' }),
  })
  return (
    <Modal
      open
      onClose={onClose}
      title="Criar uma coleção"
      width={460}
      footer={
        <>
          <span style={{ flex: 1 }} />
          <Button variant="primary" disabled={nome.trim() === ''} loading={criar.isPending} onClick={() => criar.mutate()}>
            Criar
          </Button>
        </>
      }
    >
      <div className="dc__share">
        <p className="dc__share-dica">Coleções são usadas para agrupar documentos e definir permissões.</p>
        <Input
          autoFocus
          value={nome}
          placeholder="Nome"
          onChange={(e) => setNome(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && nome.trim()) criar.mutate()
          }}
        />
        <label className="dc__coltoggle">
          <span className="dc__colperm-rotulo">
            Permissão{' '}
            <i
              className="far fa-question-circle"
              title="O que os membros do workspace podem fazer nos documentos desta coleção, sem convite individual."
              aria-hidden
            />
          </span>
          <Select value={permissao} options={OPCOES_PERMISSAO} onChange={(v) => setPermissao(v as 'none' | 'view' | 'edit')} />
          <span className="dc__coltoggle-dica" style={{ marginLeft: 0 }}>
            Sem acesso: só o dono, o admin e os convidados de cada documento enxergam.
          </span>
        </label>
        <label className="dc__coltoggle">
          <Checkbox checked={publico} onChange={(e) => setPublico(e.target.checked)} label="Compartilhamento de documentos públicos" />
          <span className="dc__coltoggle-dica">Permitir que documentos desta coleção sejam compartilhados publicamente na internet.</span>
        </label>
        <label className="dc__coltoggle">
          <Checkbox checked={comentando} onChange={(e) => setComentando(e.target.checked)} label="Comentando" />
          <span className="dc__coltoggle-dica">Permitir comentários em documentos desta coleção.</span>
        </label>
      </div>
    </Modal>
  )
}

// ---------------------------------------------------------------------------
// Editar coleção: nome + permissões (modelo Outline)
// ---------------------------------------------------------------------------

function EditarColecaoModal({ colecao, onClose, aoMudar }: { colecao: Colecao; onClose: () => void; aoMudar: () => void }) {
  const toast = useToast()
  const [nome, setNome] = useState(colecao.name)
  const [permissao, setPermissao] = useState<'none' | 'view' | 'edit'>(colecao.defaultLevel ?? 'view')
  const [publico, setPublico] = useState(colecao.allowPublicShare)
  const [comentando, setComentando] = useState(colecao.allowComments)
  const salvar = useMutation({
    mutationFn: async () =>
      (
        await api.documents.folders[':id'].$patch({
          param: { id: colecao.id },
          json: { name: nome.trim(), defaultLevel: permissao, allowPublicShare: publico, allowComments: comentando },
        })
      ).json(),
    onSuccess: () => {
      toast.push({ tone: 'success', message: 'Coleção salva.' })
      aoMudar()
      onClose()
    },
    onError: (e) => toast.push({ tone: 'danger', message: e instanceof Error ? e.message : 'Falha ao salvar a coleção.' }),
  })
  return (
    <Modal
      open
      onClose={onClose}
      title="Editar coleção"
      width={460}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button variant="primary" disabled={nome.trim() === ''} loading={salvar.isPending} onClick={() => salvar.mutate()}>
            Salvar
          </Button>
        </>
      }
    >
      <div className="dc__share">
        <p className="dc__share-dica">Coleções são usadas para agrupar documentos e definir permissões.</p>
        <Input autoFocus value={nome} onChange={(e) => setNome(e.target.value)} placeholder="Nome da coleção" />
        <label className="dc__coltoggle">
          <span className="dc__colperm-rotulo">Permissão</span>
          <Select value={permissao} options={OPCOES_PERMISSAO} onChange={(v) => setPermissao(v as 'none' | 'view' | 'edit')} />
        </label>
        <label className="dc__coltoggle">
          <Checkbox checked={publico} onChange={(e) => setPublico(e.target.checked)} label="Compartilhamento de documentos públicos" />
          <span className="dc__coltoggle-dica">Permitir que documentos desta coleção sejam compartilhados publicamente na internet.</span>
        </label>
        <label className="dc__coltoggle">
          <Checkbox checked={comentando} onChange={(e) => setComentando(e.target.checked)} label="Comentando" />
          <span className="dc__coltoggle-dica">Permitir comentários em documentos desta coleção (recurso em evolução).</span>
        </label>
      </div>
    </Modal>
  )
}

// ---------------------------------------------------------------------------
// Documento aberto: titulo + editor + compartilhar + exportar + sumário
// ---------------------------------------------------------------------------

function DocumentoAberto({
  docId,
  colecoes,
  aoMudar,
  aoExcluir,
  aoAbrir,
  membros,
  meuId,
}: {
  docId: string
  colecoes: Colecao[]
  aoMudar: () => void
  aoExcluir: () => void
  aoAbrir: (id: string) => void
  membros: { id: string; name: string }[]
  meuId: string
}) {
  const toast = useToast()
  const confirm = useConfirm()
  const qc = useQueryClient()
  const detalhe = useQuery({
    queryKey: ['document', docId],
    queryFn: async () => (await api.documents[':id'].$get({ param: { id: docId } })).json(),
  })
  const [titulo, setTitulo] = useState<string | null>(null)
  const [compartilhar, setCompartilhar] = useState(false)
  const [historicoAberto, setHistoricoAberto] = useState(false)
  const [palavrasAoVivo, setPalavrasAoVivo] = useState<number | null>(null)
  /** Menu "..." da barra (estilo Outline). */
  const [maisAberto, setMaisAberto] = useState(false)
  /** Item ATIVO do Conteudo: marcado no clique e acompanhando a rolagem. */
  const [tocAtivo, setTocAtivo] = useState(0)
  /** Largura do painel Conteudo — ajustavel pela alca e lembrada no browser. */
  const [tocLargura, setTocLargura] = useState(() => {
    try {
      const n = Number(localStorage.getItem('orbind.docs.toc.largura'))
      return n >= 140 && n <= 380 ? n : 200
    } catch {
      return 200
    }
  })
  /**
   * O Conteudo e uma coluna AO LADO do texto (nunca por baixo dele): a area
   * e medida e a largura efetiva para antes de o texto ficar mais estreito
   * que TEXTO_MINIMO. Sem lugar nem para o Conteudo minimo, ele some.
   */
  const [mioloEl, setMioloEl] = useState<HTMLDivElement | null>(null)
  const [mioloLargura, setMioloLargura] = useState(0)
  useEffect(() => {
    if (!mioloEl) return
    const medir = () => setMioloLargura(mioloEl.clientWidth)
    medir()
    const observador = new ResizeObserver(medir)
    observador.observe(mioloEl)
    return () => observador.disconnect()
  }, [mioloEl])
  const tocCabe = mioloLargura > 0 ? larguraMaximaDoConteudo(mioloLargura) : TOC_MAX
  const tocEfetiva = Math.min(tocLargura, tocCabe)
  const iniciarArrasteToc = (e: React.MouseEvent) => {
    e.preventDefault()
    const x0 = e.clientX
    const l0 = tocEfetiva
    const limite = Math.min(TOC_MAX, tocCabe)
    const prender = (l: number) => Math.min(limite, Math.max(TOC_MIN, l))
    const mover = (ev: MouseEvent) => {
      setTocLargura(prender(l0 + (ev.clientX - x0)))
    }
    const soltar = (ev: MouseEvent) => {
      document.removeEventListener('mousemove', mover)
      document.removeEventListener('mouseup', soltar)
      const fim = prender(l0 + (ev.clientX - x0))
      try {
        localStorage.setItem('orbind.docs.toc.largura', String(fim))
      } catch {
        /* sem storage: fica so na sessao */
      }
    }
    document.addEventListener('mousemove', mover)
    document.addEventListener('mouseup', soltar)
  }
  const paginaRef = useRef<HTMLDivElement | null>(null)
  useEffect(() => {
    const pagina = paginaRef.current
    if (!pagina) return
    // throttle por TIMER (nao rAF: paineis ocultos nao geram frames)
    let agendado = 0
    const aoRolar = () => {
      if (agendado) return
      agendado = window.setTimeout(() => {
        agendado = 0
        const titulos = [...pagina.querySelectorAll('.dc__coluna .ProseMirror h1, .dc__coluna .ProseMirror h2, .dc__coluna .ProseMirror h3')]
        const topo = pagina.getBoundingClientRect().top + 90
        let ativo = 0
        titulos.forEach((h, i) => {
          if (h.getBoundingClientRect().top <= topo) ativo = i
        })
        setTocAtivo(ativo)
      }, 80)
    }
    pagina.addEventListener('scroll', aoRolar, { passive: true })
    return () => {
      pagina.removeEventListener('scroll', aoRolar)
      window.clearTimeout(agendado)
    }
    // o ref so existe DEPOIS do carregamento — rearma quando a pagina monta
  }, [detalhe.isPending])
  const [comentariosAbertos, setComentariosAbertos] = useState(false)
  /** Leitura (nivel view) tambem ganha as decoracoes do editor. */
  const leituraRef = useRef<HTMLDivElement | null>(null)
  useEffect(() => {
    if (leituraRef.current) decorarLeituraRte(leituraRef.current)
  })

  const patch = useMutation({
    mutationFn: async (json: { title?: string; content?: string; folderId?: string | null; shareMode?: 'todos' | 'escolhidos' }) =>
      (await api.documents[':id'].$patch({ param: { id: docId }, json })).json(),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['document', docId] })
      aoMudar()
    },
    onError: (e) => toast.push({ tone: 'danger', message: e instanceof Error ? e.message : 'Falha ao salvar.' }),
  })
  const excluir = useMutation({
    mutationFn: async () => (await api.documents[':id'].$delete({ param: { id: docId } })).json(),
    onSuccess: () => {
      toast.push({ tone: 'success', message: 'Documento excluído.' })
      aoMudar()
      aoExcluir()
    },
    onError: (e) => toast.push({ tone: 'danger', message: e instanceof Error ? e.message : 'Falha ao excluir.' }),
  })
  const duplicar = useMutation({
    mutationFn: async () => (await api.documents[':id'].duplicate.$post({ param: { id: docId } })).json(),
    onSuccess: (d) => {
      aoMudar()
      const copia = (d as { document?: { id: string } }).document
      toast.push({ tone: 'success', message: 'Documento duplicado.' })
      if (copia) aoAbrir(copia.id)
    },
    onError: (e) => toast.push({ tone: 'danger', message: e instanceof Error ? e.message : 'Falha ao duplicar.' }),
  })
  const aninhar = useMutation({
    mutationFn: async () =>
      (await api.documents.$post({ json: { title: 'Sem título', parentDocumentId: docId } })).json(),
    onSuccess: (d) => {
      aoMudar()
      const novo = (d as { document?: { id: string } }).document
      if (novo) aoAbrir(novo.id)
    },
  })
  const publicar = useMutation({
    mutationFn: async () => (await api.documents[':id'].publish.$post({ param: { id: docId } })).json(),
    onSuccess: (d) => {
      const v = (d as { version?: number }).version
      toast.push({ tone: 'success', message: v ? `Versão ${v} publicada.` : 'Documento publicado.' })
      void qc.invalidateQueries({ queryKey: ['document', docId] })
      aoMudar()
    },
    onError: (e) => toast.push({ tone: 'danger', message: e instanceof Error ? e.message : 'Falha ao publicar.' }),
  })

  if (detalhe.isPending) return <div className="dc__carregando">Carregando…</div>
  const dados = detalhe.data as
    | {
        document: {
          id: string
          title: string
          content: string
          folderId: string | null
          coverFolderId: string | null
          shareMode: 'todos' | 'escolhidos'
          ownerMemberId: string
          shareToken: string | null
          updatedAt: string
          publishedVersion: number
          publishedAt: string | null
          temRascunho: boolean
        }
        meuNivel: Nivel
        permissions: { memberId: string; level: string; name: string }[]
      }
    | undefined
  if (!dados?.document) {
    return <EmptyState icon="fas fa-lock" title="Sem acesso" description="Este documento não existe mais ou você não tem acesso a ele." />
  }
  const doc = dados.document
  /** Este doc E uma colecao (capa): excluir/mover/modo ficam na colecao. */
  const ehCapa = doc.coverFolderId !== null
  const nivel = dados.meuNivel
  const podeEditar = nivel === 'edit' || nivel === 'delete' || nivel === 'owner'
  const podeExcluir = nivel === 'delete' || nivel === 'owner'
  const dono = nivel === 'owner'
  const stats = estatisticasDe(doc.content)
  const colecaoDoDoc = colecoes.find((c) => c.id === doc.folderId) ?? null

  /** Sumário: títulos H1-H3 do conteúdo, clicáveis (rola até o trecho). */
  const sumario = (() => {
    const tmp = document.createElement('div')
    tmp.innerHTML = doc.content
    return [...tmp.querySelectorAll('h1, h2, h3')].map((h, i) => ({
      i,
      nivel: Number(h.tagName[1]),
      texto: h.textContent ?? '',
    }))
  })()
  const irParaTitulo = (indice: number) => {
    setTocAtivo(indice)
    const alvo = [...document.querySelectorAll('.dc__coluna .ProseMirror h1, .dc__coluna .ProseMirror h2, .dc__coluna .ProseMirror h3')][indice]
    alvo?.scrollIntoView({ block: 'start' })
  }

  const baixarWord = async () => {
    const r = await fetch(`${apiBase()}/documents/${doc.id}/word`, {
      headers: { authorization: `Bearer ${webTokenStorage.get() ?? ''}` },
    })
    if (!r.ok) return toast.push({ tone: 'danger', message: 'Falha ao exportar.' })
    const blob = await r.blob()
    const a = document.createElement('a')
    a.href = URL.createObjectURL(blob)
    a.download = `${doc.title.replace(/[\\/:*?"<>|]/g, '')}.doc`
    a.click()
    URL.revokeObjectURL(a.href)
  }

  const imprimirPdf = () => {
    const w = window.open('', '_blank', 'width=900,height=700')
    if (!w) return
    w.document.write(`<!doctype html><html lang="pt-BR"><head><meta charset="utf-8"><title>${doc.title.replace(/</g, '&lt;')}</title>
<style>body{font-family:Arial,Helvetica,sans-serif;color:#111;line-height:1.6;font-size:12pt;max-width:794px;margin:0 auto;padding:24px}img{max-width:100%}table{border-collapse:collapse;width:100%}td,th{border:1px solid #888;padding:6px 10px}h1{font-size:20pt}
.rte-callout{border:1px solid #bbb;border-radius:8px;padding:10px 14px;margin:10px 0}
.rte-callout--info{background:#eaf1ff}.rte-callout--aviso{background:#fff6df}.rte-callout--ok{background:#e9f9ef}
pre.rte-code{background:#f4f5fb;border:1px solid #ccc;border-radius:8px;padding:10px 14px;font-family:Consolas,monospace;font-size:10.5pt;white-space:pre-wrap}
code{background:#eef0fa;border-radius:4px;padding:1px 5px;font-family:Consolas,monospace}
ul.rte-todo{list-style:none;padding-left:4px}ul.rte-todo li.rte-feito{color:#888;text-decoration:line-through}
ul[data-type="taskList"]{list-style:none;padding-left:4px}ul[data-type="taskList"] li{display:flex;gap:8px}
ul[data-type="taskList"] li[data-checked="true"] div{color:#888;text-decoration:line-through}
.rte-mention{color:#1b4df5;font-weight:600}
div[data-youtube-video] iframe{border:0;max-width:100%;aspect-ratio:16/9;width:640px;height:auto}</style>
</head><body><h1>${doc.title.replace(/</g, '&lt;')}</h1>${doc.content}</body></html>`)
    w.document.close()
    w.focus()
    setTimeout(() => w.print(), 350)
  }

  const opcoesColecao = [{ value: '', label: '— Sem coleção' }, ...colecoes.map((c) => ({ value: c.id, label: c.name }))]

  return (
    <div className="dc__doc-area dc__doc-area--pagina">
      {/* barra FINA no topo (estilo Outline): trilha à esquerda, ações à direita */}
      <div className="dc__barra">
        <span className="dc__barra-trilha" title={doc.shareMode === 'escolhidos' ? 'Só você e as pessoas escolhidas veem' : (colecaoDoDoc ? `Coleção ${colecaoDoDoc.name}` : 'Documento da empresa')}>
          {doc.shareMode === 'escolhidos' ? (
            <i className="fas fa-lock" aria-hidden style={{ color: '#b7791f' }} />
          ) : (
            <i className={colecaoDoDoc?.icon || 'fas fa-folder'} aria-hidden style={{ color: colecaoDoDoc?.color ?? 'var(--ob-primary)' }} />
          )}
          <span className="dc__barra-nome">{ehCapa ? 'Coleção' : doc.shareMode === 'escolhidos' ? 'Pessoas escolhidas' : (colecaoDoDoc?.name ?? 'Empresa')}</span>
          <span className="dc__barra-sep" aria-hidden>/</span>
          <span className="dc__barra-doc">{doc.title}</span>
        </span>
        <span style={{ flex: 1 }} />
        {patch.isPending ? <span className="dc__salvando">Salvando…</span> : null}
        {podeEditar ? (
          doc.temRascunho ? (
            <span className="dc__pub-chip dc__pub-chip--rascunho" title={doc.publishedVersion > 0 ? `Alterações ainda não publicadas (publicado: v${doc.publishedVersion})` : 'Ainda não publicado — só quem edita vê'}>
              Rascunho
            </span>
          ) : (
            <span className="dc__pub-chip" title={doc.publishedAt ? `Publicado em ${formatData(doc.publishedAt)}` : undefined}>
              Publicado v{doc.publishedVersion}
            </span>
          )
        ) : null}
        {dono ? (
          <Button variant="secondary" onClick={() => setCompartilhar(true)}>
            Compartilhar
          </Button>
        ) : null}
        {podeEditar ? (
          <Button variant="secondary" icon="fas fa-plus" onClick={() => aninhar.mutate()}>
            Novo documento
          </Button>
        ) : null}
        {podeEditar ? (
          <Button
            variant="primary"
            loading={publicar.isPending}
            disabled={!doc.temRascunho}
            onClick={() => publicar.mutate()}
            title={doc.temRascunho ? 'Publicar o rascunho: vira uma versão e fica visível para quem tem o compartilhamento' : 'Sem alterações para publicar'}
          >
            Publicar
          </Button>
        ) : null}
        <div className="dc__mais">
          <button type="button" className="dc__mais-btn" aria-label="Mais ações" title="Mais ações" onClick={() => setMaisAberto((v) => !v)}>
            <i className="fas fa-ellipsis-h" aria-hidden />
          </button>
          {maisAberto ? (
            <>
              <div className="dc__mais-fundo" onClick={() => setMaisAberto(false)} aria-hidden />
              <div className="dc__mais-menu" role="menu">
                <button type="button" onClick={() => { setMaisAberto(false); setHistoricoAberto(true) }}>
                  <i className="fas fa-history" aria-hidden /> Histórico
                </button>
                {(colecaoDoDoc?.allowComments ?? true) ? (
                  <button type="button" onClick={() => { setMaisAberto(false); setComentariosAbertos((v) => !v) }}>
                    <i className="far fa-comment" aria-hidden /> Comentários
                  </button>
                ) : null}
                <button type="button" onClick={() => { setMaisAberto(false); duplicar.mutate() }}>
                  <i className="far fa-copy" aria-hidden /> Duplicar
                </button>
                <div className="dc__mais-sep" aria-hidden />
                <button type="button" onClick={() => { setMaisAberto(false); void baixarWord() }}>
                  <i className="fas fa-file-word" aria-hidden /> Baixar Word
                </button>
                <button type="button" onClick={() => { setMaisAberto(false); imprimirPdf() }}>
                  <i className="fas fa-file-pdf" aria-hidden /> Baixar PDF
                </button>
                {dono && doc.shareMode !== 'escolhidos' && !ehCapa ? (
                  <>
                    <div className="dc__mais-sep" aria-hidden />
                    <div className="dc__mais-campo">
                      <span>Coleção</span>
                      <Select value={doc.folderId ?? ''} options={opcoesColecao} onChange={(v) => patch.mutate({ folderId: v || null })} />
                    </div>
                  </>
                ) : null}
                {podeExcluir && !ehCapa ? (
                  <>
                    <div className="dc__mais-sep" aria-hidden />
                    <button
                      type="button"
                      className="dc__mais-perigo"
                      onClick={() => {
                        setMaisAberto(false)
                        confirm.ask(`Excluir o documento “${doc.title}”? Documentos aninhados sobem um nível.`, () => excluir.mutate())
                      }}
                    >
                      <i className="far fa-trash-alt" aria-hidden /> Excluir
                    </button>
                  </>
                ) : null}
              </div>
            </>
          ) : null}
        </div>
      </div>

      {/* a PÁGINA: a área de trabalho inteira é o documento (sem moldura) */}
      <div className="dc__linha">
      <div className="dc__pagina" ref={paginaRef}>
        <div
          className="dc__miolo"
          ref={setMioloEl}
          style={{ '--dc-toc-largura': `${tocEfetiva}px` } as CSSProperties}
        >
          {/* Conteudo na margem esquerda, em coluna propria: o texto fica
              centrado enquanto cabe e nunca passa por cima dele */}
          {sumario.length > 0 && tocEfetiva >= TOC_MIN ? (
            <div className="dc__tocwrap">
              <nav className="dc__toc dc__toc--esq" style={{ width: tocEfetiva }} aria-label="Conteúdo do documento">
                <span className="dc__toc-tit">Conteúdo</span>
                <span
                  className="dc__toc-alca"
                  role="separator"
                  aria-orientation="vertical"
                  title="Arraste para ajustar a largura"
                  onMouseDown={iniciarArrasteToc}
                />
                {(() => {
                  // indentacao RELATIVA: o menor nivel presente encosta na
                  // margem; os niveis abaixo entram por dentro (H2 sob H1…)
                  const nivelBase = Math.min(...sumario.map((x) => x.nivel))
                  return sumario.map((h) => (
                    <button
                      key={h.i}
                      type="button"
                      className={h.i === tocAtivo ? 'dc__toc-on' : ''}
                      style={{ paddingLeft: 8 + (h.nivel - nivelBase) * 14 }}
                      onClick={() => irParaTitulo(h.i)}
                      title={h.texto}
                    >
                      {h.texto}
                    </button>
                  ))
                })()}
              </nav>
            </div>
          ) : null}
          <div className="dc__coluna">
            {podeEditar ? (
              <input
                className="dc__titulo"
                value={titulo ?? doc.title}
                placeholder="Sem título"
                onChange={(e) => setTitulo(e.target.value)}
                onBlur={() => {
                  const novo = (titulo ?? doc.title).trim()
                  if (novo && novo !== doc.title) patch.mutate({ title: novo })
                  setTitulo(null)
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') (e.target as HTMLInputElement).blur()
                }}
                aria-label="Título do documento"
              />
            ) : (
              <h1 className="dc__titulo dc__titulo--ro">{doc.title}</h1>
            )}
            <div className="dc__metalinha">
              <span>Atualizado em {formatData(doc.updatedAt)}</span>
              <span aria-hidden>•</span>
              {(colecaoDoDoc?.allowComments ?? true) ? (
                <button type="button" className="dc__metalink" onClick={() => setComentariosAbertos((v) => !v)}>
                  <i className="far fa-comment" aria-hidden /> Comentar
                </button>
              ) : null}
              <span aria-hidden>•</span>
              <span title="Estatísticas">
                {palavrasAoVivo ?? stats.palavras} palavra{(palavrasAoVivo ?? stats.palavras) === 1 ? '' : 's'} · ~
                {Math.max(1, Math.round((palavrasAoVivo ?? stats.palavras) / 200))} min
              </span>
              <span aria-hidden>•</span>
              <span>{NIVEL_ROTULO[nivel]}</span>
            </div>
            {/* editor sobre ProseMirror com AUTOSAVE: preenche até o fim */}
            <DocsEditor
              conteudo={doc.content}
              editavel={podeEditar}
              mencoes={membros}
              aoSalvar={(html) => patch.mutate({ content: html })}
              aoMedir={setPalavrasAoVivo}
            />
          </div>
        </div>
      </div>
      {/* Comentários: doca à DIREITA em altura total (estilo Outline) */}
      {comentariosAbertos ? <ComentariosPanel docId={doc.id} meuId={meuId} dono={dono} onClose={() => setComentariosAbertos(false)} /> : null}
      </div>

      {historicoAberto ? (
        <HistoricoModal
          docId={doc.id}
          onClose={() => setHistoricoAberto(false)}
          aoRestaurar={() => {
            void qc.invalidateQueries({ queryKey: ['document', docId] })
            aoMudar()
            setHistoricoAberto(false)
          }}
        />
      ) : null}
      {compartilhar ? (
        <CompartilharModal
          docId={doc.id}
          docTitulo={doc.title}
          ehCapa={ehCapa}
          shareMode={doc.shareMode}
          shareToken={doc.shareToken}
          publicoPermitido={colecaoDoDoc ? colecaoDoDoc.allowPublicShare : true}
          permissions={dados.permissions}
          membros={membros.filter((m) => m.id !== doc.ownerMemberId && m.id !== meuId)}
          onClose={() => setCompartilhar(false)}
          aoMudar={() => {
            void qc.invalidateQueries({ queryKey: ['document', docId] })
            aoMudar()
          }}
        />
      ) : null}
      {confirm.element}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Historico de versoes (estilo Outline): lista, previa e restaurar
// ---------------------------------------------------------------------------

function HistoricoModal({ docId, onClose, aoRestaurar }: { docId: string; onClose: () => void; aoRestaurar: () => void }) {
  const toast = useToast()
  const confirm = useConfirm()
  const [versaoAberta, setVersaoAberta] = useState<string | null>(null)
  const versoes = useQuery({
    queryKey: ['document', docId, 'revisions'],
    queryFn: async () => (await api.documents[':id'].revisions.$get({ param: { id: docId } })).json(),
  })
  const versao = useQuery({
    queryKey: ['document', docId, 'revisions', versaoAberta],
    enabled: versaoAberta !== null,
    queryFn: async () =>
      (await api.documents[':id'].revisions[':revId'].$get({ param: { id: docId, revId: versaoAberta! } })).json(),
  })
  const restaurar = useMutation({
    mutationFn: async (revId: string) =>
      (await api.documents[':id'].revisions[':revId'].restore.$post({ param: { id: docId, revId } })).json(),
    onSuccess: () => {
      toast.push({ tone: 'success', message: 'Versão restaurada — a anterior foi para o histórico.' })
      aoRestaurar()
    },
    onError: (e) => toast.push({ tone: 'danger', message: e instanceof Error ? e.message : 'Falha ao restaurar.' }),
  })
  const previaRef = useRef<HTMLDivElement | null>(null)
  useEffect(() => {
    if (previaRef.current) decorarLeituraRte(previaRef.current)
  })
  const lista = (versoes.data as { revisions?: { id: string; title: string; versao: number | null; createdAt: string; autor: string | null }[] } | undefined)?.revisions ?? []
  const conteudoVersao = (versao.data as { revision?: { content: string; title: string } } | undefined)?.revision
  return (
    <Modal
      open
      onClose={onClose}
      title="Histórico do documento"
      width={860}
      resizable
      sizeKey="doc-historico"
      footer={
        <>
          <span style={{ flex: 1 }} />
          <Button variant="ghost" onClick={onClose}>
            Fechar
          </Button>
          {versaoAberta ? (
            <Button
              variant="primary"
              icon="fas fa-undo"
              loading={restaurar.isPending}
              onClick={() =>
                confirm.ask('Restaurar esta versão? O conteúdo atual vira uma versão no histórico.', () =>
                  restaurar.mutate(versaoAberta),
                )
              }
            >
              Restaurar esta versão
            </Button>
          ) : null}
        </>
      }
    >
      <div className="dc__hist">
        <div className="dc__hist-lista">
          {lista.length === 0 ? <p className="dc__vazio">Sem versões ainda — elas surgem a cada salvamento do conteúdo.</p> : null}
          {lista.map((v) => (
            <button
              key={v.id}
              type="button"
              className={`dc__hist-item${versaoAberta === v.id ? ' dc__hist-item--on' : ''}`}
              onClick={() => setVersaoAberta(v.id)}
            >
              <span className="dc__hist-data">
                {formatData(v.createdAt)}
                {v.versao ? <span className="dc__pub-chip" title="Versão publicada">v{v.versao}</span> : null}
              </span>
              <span className="dc__hist-autor">{v.autor ?? '—'}</span>
            </button>
          ))}
        </div>
        <div className="dc__hist-previa">
          {versaoAberta === null ? (
            <p className="dc__vazio">Escolha uma versão para ver o conteúdo daquele momento.</p>
          ) : conteudoVersao ? (
            // eslint-disable-next-line react/no-danger
            <div ref={previaRef} className="ob-rte-body dc__leitura" dangerouslySetInnerHTML={{ __html: conteudoVersao.content }} />
          ) : (
            <p className="dc__vazio">Carregando…</p>
          )}
        </div>
      </div>
      {confirm.element}
    </Modal>
  )
}

// ---------------------------------------------------------------------------
// Comentarios do documento (a colecao pode desligar)
// ---------------------------------------------------------------------------

function ComentariosPanel({ docId, meuId, dono, onClose }: { docId: string; meuId: string; dono: boolean; onClose: () => void }) {
  const toast = useToast()
  const qc = useQueryClient()
  const [texto, setTexto] = useState('')
  const comentarios = useQuery({
    queryKey: ['document', docId, 'comments'],
    queryFn: async () => (await api.documents[':id'].comments.$get({ param: { id: docId } })).json(),
  })
  const invalidar = () => void qc.invalidateQueries({ queryKey: ['document', docId, 'comments'] })
  const enviar = useMutation({
    mutationFn: async () => {
      const r = await api.documents[':id'].comments.$post({ param: { id: docId }, json: { body: texto.trim() } })
      if (!r.ok) {
        const corpo = (await r.json().catch(() => null)) as { error?: { message?: string } } | null
        throw new Error(corpo?.error?.message ?? 'Falha ao comentar.')
      }
      return r.json()
    },
    onSuccess: () => {
      setTexto('')
      invalidar()
    },
    onError: (e) => toast.push({ tone: 'danger', message: e instanceof Error ? e.message : 'Falha ao comentar.' }),
  })
  const apagar = useMutation({
    mutationFn: async (commentId: string) =>
      (await api.documents[':id'].comments[':commentId'].$delete({ param: { id: docId, commentId } })).json(),
    onSuccess: invalidar,
    onError: (e) => toast.push({ tone: 'danger', message: e instanceof Error ? e.message : 'Falha ao apagar.' }),
  })
  const lista =
    (comentarios.data as { comments?: { id: string; body: string; createdAt: string; authorMemberId: string; autor: string }[] } | undefined)
      ?.comments ?? []
  return (
    <aside className="dc__coment" aria-label="Comentários do documento">
      <div className="dc__coment-topo">
        <strong>Comentários</strong>
        <span style={{ flex: 1 }} />
        <button type="button" className="dc__coment-fechar" title="Fechar os comentários" aria-label="Fechar os comentários" onClick={onClose}>
          <i className="fas fa-chevron-right" aria-hidden />
        </button>
      </div>
      <div className="dc__coment-lista">
        {lista.length === 0 ? <p className="dc__coment-nenhum">Nenhum comentário ainda</p> : null}
        {lista.map((cm) => (
          <div key={cm.id} className="dc__coment-item">
            <span className="wi-avatar" aria-hidden>{initialsOf(cm.autor)}</span>
            <div className="dc__coment-corpo">
              <span className="dc__coment-cab">
                <strong>{cm.autor}</strong> · {formatData(cm.createdAt)}
              </span>
              <p>{cm.body}</p>
            </div>
            {cm.authorMemberId === meuId || dono ? (
              <button
                type="button"
                className="dc__share-tirar"
                title="Apagar o comentário"
                aria-label="Apagar o comentário"
                onClick={() => apagar.mutate(cm.id)}
              >
                <i className="fas fa-times" aria-hidden />
              </button>
            ) : null}
          </div>
        ))}
      </div>
      <div className="dc__coment-rodape">
        <span className="wi-avatar" aria-hidden>{initialsOf('Eu')}</span>
        <Textarea
          rows={1}
          value={texto}
          placeholder="Adicionar um comentário…"
          onChange={(e) => setTexto(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey && texto.trim()) {
              e.preventDefault()
              enviar.mutate()
            }
          }}
        />
        <button
          type="button"
          className="dc__coment-enviar"
          title="Enviar o comentário (Enter)"
          aria-label="Enviar o comentário"
          disabled={texto.trim() === '' || enviar.isPending}
          onClick={() => enviar.mutate()}
        >
          <i className="fas fa-paper-plane" aria-hidden />
        </button>
      </div>
    </aside>
  )
}

// ---------------------------------------------------------------------------
// Compartilhar: permissoes por membro + link publico
// ---------------------------------------------------------------------------

function CompartilharModal({
  docId,
  docTitulo,
  ehCapa,
  shareMode,
  shareToken,
  publicoPermitido,
  permissions,
  membros,
  onClose,
  aoMudar,
}: {
  docId: string
  docTitulo: string
  /** Capa de colecao: quem ve e definido pela Permissao da colecao. */
  ehCapa: boolean
  shareMode: 'todos' | 'escolhidos'
  shareToken: string | null
  publicoPermitido: boolean
  permissions: { memberId: string; level: string; name: string }[]
  membros: { id: string; name: string }[]
  onClose: () => void
  aoMudar: () => void
}) {
  const toast = useToast()
  const [lista, setLista] = useState<{ memberId: string; level: string }[]>(
    permissions.map((p) => ({ memberId: p.memberId, level: p.level })),
  )
  const [modo, setModo] = useState<'todos' | 'escolhidos'>(shareMode)
  const [novoMembro, setNovoMembro] = useState('')

  const salvar = useMutation({
    mutationFn: async () => {
      if (modo !== shareMode) {
        await api.documents[':id'].$patch({ param: { id: docId }, json: { shareMode: modo } })
      }
      return (
        await api.documents[':id'].permissions.$put({
          param: { id: docId },
          json: { permissions: lista as { memberId: string; level: 'view' | 'edit' | 'delete' }[] },
        })
      ).json()
    },
    onSuccess: () => {
      toast.push({ tone: 'success', message: 'Compartilhamento salvo.' })
      aoMudar()
      onClose()
    },
    onError: (e) => toast.push({ tone: 'danger', message: e instanceof Error ? e.message : 'Falha ao salvar.' }),
  })
  const gerarLink = useMutation({
    mutationFn: async () => {
      const r = await api.documents[':id']['share-link'].$post({ param: { id: docId } })
      if (!r.ok) {
        const corpo = (await r.json().catch(() => null)) as { error?: { message?: string } } | null
        throw new Error(corpo?.error?.message ?? 'Falha ao gerar o link.')
      }
      return r.json()
    },
    onSuccess: aoMudar,
    onError: (e) => toast.push({ tone: 'danger', message: e instanceof Error ? e.message : 'Falha ao gerar o link.' }),
  })
  const revogarLink = useMutation({
    mutationFn: async () => (await api.documents[':id']['share-link'].$delete({ param: { id: docId } })).json(),
    onSuccess: () => {
      toast.push({ tone: 'success', message: 'Link revogado.' })
      aoMudar()
    },
  })

  const nomeDe = (id: string) => membros.find((m) => m.id === id)?.name ?? permissions.find((p) => p.memberId === id)?.name ?? '—'
  const urlPublica = shareToken ? `${apiOrigin()}/public/doc/${shareToken}` : null

  return (
    <Modal
      open
      onClose={onClose}
      title={`Compartilhar — ${docTitulo}`}
      width={640}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button variant="primary" loading={salvar.isPending} onClick={() => salvar.mutate()}>
            Salvar
          </Button>
        </>
      }
    >
      <div className="dc__share">
        {!ehCapa ? (
          <div className="dc__mais-campo" style={{ padding: 0, marginBottom: 6 }}>
            <span className="dc__colperm-rotulo">Quem vê</span>
            <Select
              value={modo}
              options={[
                { value: 'todos', label: 'Todos com acesso à ferramenta' },
                { value: 'escolhidos', label: 'Somente pessoas escolhidas' },
              ]}
              onChange={(v) => setModo(v as 'todos' | 'escolhidos')}
            />
          </div>
        ) : null}
        <p className="dc__share-dica">
          {ehCapa
            ? 'Este documento é a própria coleção: quem vê é definido pela Permissão da coleção (lápis dela); a lista abaixo dá níveis maiores a pessoas específicas.'
            : modo === 'todos'
              ? 'Todo membro vê a última versão publicada (com o nível padrão da coleção); a lista abaixo dá níveis maiores a pessoas específicas.'
              : 'Só você e as pessoas da lista abaixo enxergam este documento — nem o administrador vê sem convite.'}{' '}
          <strong>Visualizar</strong> só lê; <strong>Editar</strong> também altera (e vê o rascunho); <strong>Excluir</strong> pode tudo.
        </p>

        {lista.map((p) => (
          <div key={p.memberId} className="dc__share-linha">
            <span className="wi-avatar" aria-hidden>{initialsOf(nomeDe(p.memberId))}</span>
            <span className="dc__share-nome">{nomeDe(p.memberId)}</span>
            <Select
              value={p.level}
              options={[
                { value: 'view', label: 'Visualizar' },
                { value: 'edit', label: 'Editar' },
                { value: 'delete', label: 'Excluir' },
              ]}
              onChange={(v) => setLista((l) => l.map((x) => (x.memberId === p.memberId ? { ...x, level: v } : x)))}
            />
            <button
              type="button"
              className="dc__share-tirar"
              title="Tirar o acesso"
              aria-label={`Tirar o acesso de ${nomeDe(p.memberId)}`}
              onClick={() => setLista((l) => l.filter((x) => x.memberId !== p.memberId))}
            >
              <i className="fas fa-times" aria-hidden />
            </button>
          </div>
        ))}

        <div className="dc__share-add">
          <Select
            value={novoMembro}
            placeholder="Adicionar pessoa…"
            options={membros
              .filter((m) => !lista.some((p) => p.memberId === m.id))
              .map((m) => ({ value: m.id, label: m.name }))}
            onChange={(v) => {
              if (!v) return
              setLista((l) => [...l, { memberId: v, level: 'view' }])
              setNovoMembro('')
            }}
          />
        </div>

        <div className="dc__share-link">
          <span className="dc__secao-tit">Link público</span>
          {urlPublica ? (
            <div className="dc__share-linha">
              <input className="dc__share-url" readOnly value={urlPublica} onFocus={(e) => e.target.select()} />
              <Button
                variant="secondary"
                icon="far fa-copy"
                onClick={() => {
                  void navigator.clipboard.writeText(urlPublica)
                  toast.push({ tone: 'success', message: 'Link copiado.' })
                }}
              >
                Copiar
              </Button>
              <Button variant="danger" loading={revogarLink.isPending} onClick={() => revogarLink.mutate()}>
                Revogar
              </Button>
            </div>
          ) : publicoPermitido ? (
            <div>
              <Button variant="secondary" icon="fas fa-link" loading={gerarLink.isPending} onClick={() => gerarLink.mutate()}>
                Gerar link público
              </Button>
              <p className="dc__share-dica">Quem tiver o link lê o documento (sem login) e pode baixar em Word/PDF.</p>
            </div>
          ) : (
            <p className="dc__share-dica">A coleção deste documento não permite compartilhamento público.</p>
          )}
        </div>
      </div>
    </Modal>
  )
}
